import { redactUrl } from "@/utils/redactUrl";

/**
 * QuickTracking（阿里云）自定义事件上报封装。
 *
 * SDK 本身只自动采集 PV（见 components/Common/QuickTracking/Index.tsx 的 enableHistory），
 * 点击行为必须由业务侧主动 record，或者由本文件的全局点击代理兜底上报。
 *
 * 两种用法：
 * 1. 显式上报：业务代码里调 trackClick("mall_add_to_cart", { goods_id })，语义与参数最准确。
 * 2. 自动兜底：给元素加 data-qt="事件名"，或者什么都不加——
 *    initAutoClickTracking 会捕获页面上所有按钮/链接的点击并以 auto_click 上报。
 */

// 与 next.config.js 的 basePath 保持一致，用于从 location.pathname 里剥离
const BASE_PATH = "/pointsmall";

// 与 next-i18next.config.js 的 locales 保持一致，用于从路径里剥离语言前缀
const LOCALES = ["sc", "tc", "en", "jp", "kr", "es", "pt", "th", "vn", "ar", "ug", "hi"];

// 未显式命名的点击统一用这个 logkey，靠属性（page_path / el_selector / el_text）做区分
export const AUTO_CLICK_LOGKEY = "auto_click";

export type QtEventType = "CLK" | "EXP" | "OTHER";

export type QtProps = Record<string, string | number | boolean | null | undefined>;

/**
 * 往 aplus_queue 里塞一条指令。
 * SDK 是 afterInteractive 异步加载的，队列在 SDK ready 之前会先缓存，所以这里不需要判断 SDK 是否就绪。
 */
const pushAplus = (action: string, args: unknown[]) => {
  if (typeof window === "undefined") return;
  const queue = ((window as any).aplus_queue = (window as any).aplus_queue || []);
  queue.push({ action, arguments: args });
};

/**
 * 属性归一化：丢掉空值，非数字一律转字符串。
 * QuickTracking 对嵌套对象的处理不稳定，统一拍平成标量。
 */
const normalizeProps = (props?: QtProps) => {
  const out: Record<string, string | number> = {};
  if (!props) return out;
  Object.keys(props).forEach((key) => {
    const value = props[key];
    if (value === undefined || value === null || value === "") return;
    out[key] = typeof value === "number" ? value : String(value);
  });
  return out;
};

/**
 * 当前页面路径，剥掉 basePath 和语言前缀，并把数字段归一成 :id。
 * 不归一的话 /mall/123、/mall/456 会散成无数个值，没法聚合。
 */
export const getTrackPagePath = () => {
  if (typeof window === "undefined") return "";
  let path = window.location.pathname || "/";
  if (path.startsWith(BASE_PATH)) path = path.slice(BASE_PATH.length) || "/";
  const segments = path.split("/").filter(Boolean);
  if (segments.length && LOCALES.includes(segments[0])) segments.shift();
  const normalized = segments.map((seg) => (/^\d+$/.test(seg) ? ":id" : seg));
  return "/" + normalized.join("/");
};

/**
 * 上报一条自定义事件。
 * @param logkey 事件名，需与数据侧约定，建议 模块_动作 格式，如 mall_add_to_cart
 * @param type CLK 点击 / EXP 曝光 / OTHER 其它
 * @param props 事件属性
 */
export const trackEvent = (logkey: string, type: QtEventType, props?: QtProps) => {
  if (!logkey) return;
  pushAplus("aplus.record", [
    logkey,
    type,
    {
      page_path: getTrackPagePath(),
      ...normalizeProps(props),
    },
  ]);
};

/** 上报点击事件 */
export const trackClick = (logkey: string, props?: QtProps) => trackEvent(logkey, "CLK", props);

/** 上报曝光事件 */
export const trackExpose = (logkey: string, props?: QtProps) => trackEvent(logkey, "EXP", props);

/* -------------------------------------------------------------------------- */
/*                              全局点击代理兜底                                 */
/* -------------------------------------------------------------------------- */

// 认定为“可点击”的元素。项目里大量用 <div onClick> + cursor-pointer 当按钮，所以必须带上 .cursor-pointer
const CLICKABLE_SELECTOR = [
  "[data-qt]",
  "button",
  "a",
  "[role='button']",
  "input[type='button']",
  "input[type='submit']",
  ".ant-btn",
  ".adm-button",
  ".ant-select-item",
  ".adm-tabs-tab",
  ".cursor-pointer",
].join(",");

// 从点击目标往上找可点击祖先时的最大层数，避免整页扫描
const MAX_LOOKUP_DEPTH = 12;
// data-qt-* 业务参数向上继承的最大层数
const MAX_PROP_INHERIT_DEPTH = 8;
// 文案截断长度，防止把整段富文本塞进上报
const MAX_TEXT_LENGTH = 40;

const matches = (el: Element, selector: string) => {
  try {
    return typeof el.matches === "function" && el.matches(selector);
  } catch {
    return false;
  }
};

/** 从点击落点往上找最近的可点击元素（显式 data-qt 与原生按钮同等优先，就近命中） */
const findClickTarget = (start: Element | null): HTMLElement | null => {
  let el: Element | null = start;
  let depth = 0;
  while (el && depth < MAX_LOOKUP_DEPTH && el !== document.body) {
    if (matches(el, CLICKABLE_SELECTOR)) return el as HTMLElement;
    el = el.parentElement;
    depth += 1;
  }
  return null;
};

/** data-qt-goods-id -> goods_id */
const datasetKeyToProp = (key: string) =>
  key
    .slice(2) // 去掉 dataset 里的 qt 前缀
    .replace(/^[A-Z]/, (c) => c.toLowerCase())
    .replace(/[A-Z]/g, (c) => "_" + c.toLowerCase());

/**
 * 收集 data-qt-* 业务参数，从命中元素一路往上继承。
 * 这样商品卡片可以把 goods_id 挂在外层容器，里面的收藏、加购按钮都能自动带上。
 * 就近优先：子元素上的同名参数覆盖祖先的。
 */
const collectQtProps = (el: HTMLElement) => {
  const props: QtProps = {};
  let node: HTMLElement | null = el;
  let depth = 0;
  let scope = "";
  while (node && depth < MAX_PROP_INHERIT_DEPTH && node !== document.body) {
    const dataset = node.dataset || {};
    Object.keys(dataset).forEach((key) => {
      if (key === "qt" || !key.startsWith("qt")) return;
      const prop = datasetKeyToProp(key);
      // 已有值来自更靠近点击点的元素，不覆盖
      if (prop && props[prop] === undefined) props[prop] = dataset[key];
    });
    // 记录命中元素之上最近的一个具名容器，便于归因到卡片/模块
    if (!scope && node !== el && dataset.qt) scope = dataset.qt;
    node = node.parentElement;
    depth += 1;
  }
  if (scope) props.el_scope = scope;
  return props;
};

/** 生成一个相对稳定的选择器路径，用于给未具名的点击做聚合 */
const buildSelector = (el: HTMLElement) => {
  const parts: string[] = [];
  let node: HTMLElement | null = el;
  let depth = 0;
  while (node && depth < 4 && node !== document.body) {
    const tag = (node.tagName || "").toLowerCase();
    const parent = node.parentElement;
    let index = 1;
    if (parent) {
      const siblings = Array.prototype.filter.call(parent.children, (c: Element) => c.tagName === node!.tagName);
      index = Array.prototype.indexOf.call(siblings, node) + 1;
    }
    parts.unshift(index > 1 ? `${tag}:nth-of-type(${index})` : tag);
    node = parent;
    depth += 1;
  }
  return parts.join(">");
};

// 卡片 innerText 可能带个人信息：含 @ 的词（邮箱）、6 位以上数字（手机号、交易账号）打码；积分、价格一般不到 6 位，保留。
// 邮箱不用正则匹配：[^\s@]+@ 这类写法遇到长串无 @ 文本会回溯成平方级（Sonar S5852）。入参已按单空格归一
const maskText = (text: string) =>
  text
    .split(" ")
    .map((word) => (word.includes("@") ? "*" : word))
    .join(" ")
    .replace(/\d{6,}/g, "*");

/** 取元素可读文案，优先无障碍属性，兜底 innerText */
const getElementText = (el: HTMLElement) => {
  const raw =
    el.getAttribute("aria-label") ||
    el.getAttribute("title") ||
    el.getAttribute("alt") ||
    (el as HTMLInputElement).value ||
    el.innerText ||
    el.textContent ||
    "";
  return maskText(raw.replace(/\s+/g, " ").trim()).slice(0, MAX_TEXT_LENGTH);
};

// 同一元素短时间内重复触发的去重窗口（毫秒）
const DEDUPE_WINDOW = 300;
let lastKey = "";
let lastAt = 0;

const handleDocumentClick = (event: Event) => {
  try {
    const target = findClickTarget(event.target as Element | null);
    if (!target) return;

    const dataset = target.dataset || {};
    const logkey = dataset.qt || AUTO_CLICK_LOGKEY;
    const selector = buildSelector(target);

    const key = `${logkey}|${selector}`;
    const now = Date.now();
    if (key === lastKey && now - lastAt < DEDUPE_WINDOW) return;
    lastKey = key;
    lastAt = now;

    const disabled =
      (target as HTMLButtonElement).disabled === true ||
      target.getAttribute("aria-disabled") === "true" ||
      target.className.includes?.("ant-disabled-button");

    trackClick(logkey, {
      el_tag: (target.tagName || "").toLowerCase(),
      el_text: getElementText(target),
      el_selector: selector,
      el_href: redactUrl(target.getAttribute("href") || undefined),
      // 注意不能叫 is_auto：那是 SDK 保留字段，表示「SDK 圈选自动采集」，
      // 传了会被 SDK 提升成 r.is_auto=1，把兜底点击错误归类成圈选事件
      is_fallback: dataset.qt ? 0 : 1,
      is_disabled: disabled ? 1 : 0,
      ...collectQtProps(target),
    });
  } catch {
    // 埋点异常绝不能影响业务，静默吞掉
  }
};

/**
 * 挂载全局点击代理。
 * 用捕获阶段监听，避免业务里的 stopPropagation 把事件截掉导致漏报。
 * @returns 卸载函数
 */
export const initAutoClickTracking = () => {
  if (typeof document === "undefined") return () => {};
  document.addEventListener("click", handleDocumentClick, true);
  return () => document.removeEventListener("click", handleDocumentClick, true);
};
